import { useState } from 'react'
import { cva } from 'class-variance-authority'
import { cn } from '~/lib/utils'
import { isDevelopment } from '~/utils/misc'

import MarkDown from '~/components/Helpers/MarkDown'
import Image from '~/components/Helpers/Image'
import TertiaryLink from '~/components/UI/Buttons/TertiaryLink/TertiaryLink'
import ContactLink, {
  ContactLinkProps
} from '~/components/UI/Buttons/ContactLink/ContactLink'
import ControlButton from '~/components/UI/Buttons/ControlButton/ControlButton'
import { CardProps } from '~/components/types/card'

const cardVariants = cva('', {
  variants: {
    backgroundColour: {
      'ink-blue': 'bg-blue-d text-white [&_a]:hover:text-white',
      'mid-blue': 'bg-blue-m text-white [&_a]:hover:text-white',
      chartreuse:
        'bg-char text-blue-d [&_a]:text-blue-d [&_a]:hover:text-blue-d',
      'light-grey':
        'bg-grey-l text-blue-d [&_a]:text-blue-d [&_a]:hover:text-blue-d',
      white: 'bg-white text-blue-d [&_a]:text-blue-d [&_a]:hover:text-blue-d'
    }
  },
  defaultVariants: {
    backgroundColour: 'ink-blue'
  }
})

export type ProfileCardProps = CardProps & {
  content: {
    name: string
    role: string
    profile?: Entry & { restricted: boolean }
    restricted: boolean
    userAction?: string
    contactLinks: Array<ContactLinkProps['data']>
    fullName?: string
    jobTitle?: string
    profileImage?: Image
    briefBio?: string
    email: string
    telephone: string
    sys?: { uri: string }
  }
}

type ContainerProps = {
  children: React.ReactNode
  className?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  content: any
}

const CardContainer = ({ children, className, content }: ContainerProps) => {
  const { profile, userAction, expandedCopy, contactLinks } = content

  if (
    profile?.sys.uri &&
    (userAction === 'none' ||
      (userAction === 'more-info' && !expandedCopy) ||
      (userAction === 'contact-links' && contactLinks?.length === 0))
  )
    return (
      <a
        href={profile?.sys.uri}
        className={cn(className, 'hover:[&_h4]:underline')}
      >
        {children}
      </a>
    )
  return <div className={cn(className)}>{children}</div>
}

const ProfileCard = ({
  content,
  carousel,
  // featured,
  className
}: ProfileCardProps) => {
  const profileData =
    content?.sys?.contentTypeId === 'staffProfile'
      ? {
          name: content.fullName,
          role: content.jobTitle,
          image: content.profileImage,
          profile: {
            sys: { uri: content.sys.uri },
            restricted: content?.restricted
          },
          expandedCopy: content.briefBio,
          contactLinks: [
            content.email !== null &&
              content.email.trim() !== '' && {
                link: content.email,
                label: `Email ${content.email}`,
                type: 'email',
                ariaLabel: `Click to email ${content.email}`
              },
            content.telephone !== null &&
              content.telephone.trim() !== '' && {
                link: content.telephone,
                label: `Call ${content.telephone}`,
                type: 'phone',
                ariaLabel: `Click to call ${content.telephone}`
              }
          ].filter(i => i !== false),
          backgroundColour: content.backgroundColour,
          featured: content.featured
        }
      : content

  const {
    name,
    role,
    image,
    profile,
    contactLinks,
    expandedCopy,
    backgroundColour,
    featured
  } = profileData

  const userAction = profile?.restricted
    ? 'more-info'
    : content.userAction || 'contact-links'

  const [isVisible, setIsVisible] = useState(false)
  function toggleOverlay() {
    setIsVisible(!isVisible)
  }

  const previewUrl =
    isDevelopment && !image?.asset.sys.uri.startsWith('https')
      ? 'https://preview-websitemodern-uni-hull.cloud.contensis.com'
      : ''

  const foregroundColour =
    backgroundColour === 'ink-blue'
      ? 'light-pink'
      : backgroundColour === 'mid-blue'
        ? 'white'
        : 'mid-blue'

  return (
    <CardContainer
      content={{ profile, userAction, expandedCopy, contactLinks }}
      className={cn(
        'bg-grey-l relative flex !h-[500px] max-h-[500px] w-full flex-col sm:basis-[calc(50%-12px)] lg:grow lg:basis-0',
        carousel && featured ? 'lg:flex-row' : '',
        className
      )}
    >
      <div
        className={cn(
          'flex h-[calc(100%-70px)] flex-col items-center lg:h-[calc(100%-100px)]',
          carousel && featured ? 'lg:h-full lg:min-w-1/2' : ''
        )}
      >
        {image && !profile?.restricted ? (
          <Image
            image={image}
            classes=' h-full w-full object-cover object-top'
          />
        ) : (
          <img
            className='p-l grow'
            src={`${previewUrl}/site-elements/icons/single-colour/ink-blue/user.svg`}
            alt='user icon'
          />
        )}
      </div>
      <div
        className={cn(
          cardVariants({ backgroundColour }),
          'p-xs-plus sm:p-s lg:p-m absolute bottom-0 left-0 flex w-full flex-col overflow-hidden transition-all duration-500',
          carousel && featured
            ? 'flex-col justify-end lg:relative lg:min-h-full lg:w-auto lg:min-w-1/2'
            : '',
          isVisible ? 'min-h-full' : 'min-h-0'
        )}
      >
        <h4
          className={cn(
            'mb-2xs max-w-fit',
            userAction !== 'none' && !isVisible ? 'w-[85%]' : 'w-full'
          )}
        >
          {name}
        </h4>
        {role && (
          <p
            className={cn(
              'line-clamp-2 transition-all delay-100',
              userAction !== 'none' && !isVisible ? 'w-[85%]' : 'w-full',
              carousel ? 'w-[80%]' : '',
              carousel && featured ? 'lg:w-full' : ''
            )}
          >
            {role}
          </p>
        )}

        <div
          className={cn(
            'flex flex-col justify-between overflow-hidden transition-all delay-100',
            isVisible ? 'visible opacity-100' : 'invisibile opacity-0',
            carousel && featured
              ? 'h-fit lg:visible lg:opacity-100'
              : 'h-0 grow'
          )}
        >
          {userAction === 'more-info' && expandedCopy && (
            <>
              <MarkDown className='mt-xs' content={expandedCopy} />

              {profile && (
                <TertiaryLink
                  colour={
                    ['ink-blue', 'mid-blue'].includes(backgroundColour)
                      ? 'white'
                      : 'ink-blue'
                  }
                  data={{
                    internalLink: {
                      sys: { uri: profile.sys.uri }
                    },
                    ariaLabel: `Find our more about ${name}`,
                    newWindow: undefined,
                    label: 'Find out more'
                  }}
                />
              )}
            </>
          )}
          {userAction === 'contact-links' && contactLinks?.length > 0 && (
            <div className='gap-2xs mt-s flex h-full flex-col'>
              {contactLinks.map(
                (
                  link: {
                    externalLink?: string | null | undefined
                    internalLink?: { sys: { uri: string | undefined } } | null
                    ariaLabel?: string
                    newWindow?: boolean
                    label: string | null | undefined
                    mobileLabel?: string | null | undefined
                    strongColour?: boolean
                    disabled?: boolean
                  } & { type: string; link: string },
                  index: Index
                ) => (
                  <ContactLink
                    key={index}
                    // className={'mt-xs'}
                    data={link}
                    colour={
                      ['ink-blue', 'mid-blue'].includes(backgroundColour)
                        ? 'white'
                        : 'ink-blue'
                    }
                  />
                )
              )}
              {profile && (
                <div className='flex grow items-end'>
                  <TertiaryLink
                    colour={
                      ['ink-blue', 'mid-blue'].includes(backgroundColour)
                        ? 'white'
                        : 'ink-blue'
                    }
                    data={{
                      internalLink: {
                        sys: { uri: profile.sys.uri }
                      },
                      ariaLabel: `Find our more about ${name}`,
                      newWindow: undefined,
                      label: 'Find out more'
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {((userAction === 'more-info' && expandedCopy) ||
          (userAction === 'contact-links' && contactLinks?.length > 0)) && (
          <div
            className={cn(
              'bottom-s lg:bottom-m absolute z-1 flex w-fit justify-end self-end [&_img]:transition-transform [&_img]:duration-500',
              carousel && featured ? 'lg:hidden' : '',
              isVisible ? '[&_img]:rotate-45' : ''
            )}
          >
            <ControlButton
              onClick={toggleOverlay}
              data={{
                ariaLabel: 'Control button',
                icon: {
                  altText: '',
                  asset: {
                    sys: {
                      uri: `/site-elements/icons/single-colour/${foregroundColour === 'mid-blue' ? 'white' : 'ink-blue'}/plus.svg`
                    }
                  }
                }
              }}
              colour={foregroundColour}
              size={carousel ? 'small' : 'large'}
            />
          </div>
        )}
      </div>
    </CardContainer>
  )
}

export default ProfileCard
